import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Trophy, Info } from "lucide-react";
import { API, PRIZES, EASE, fmtUsd } from "@/lib/site";
import { Countdown } from "./Countdown";
import { FadeUp, RevealLine } from "./RevealLine";
import { MonthTabs, monthLabel } from "./MonthTabs";

const fallbackEnd = () => {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1)).toISOString();
};

const Podium = ({ e, rank, delay, label, paid }) => (
  <motion.div
    initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1, ease: EASE, delay }} whileHover={{ y: -8 }}
    className={`podium podium-${rank} ${rank === 1 ? "md:-translate-y-8 md:order-2" : rank === 2 ? "md:order-1" : "md:order-3"}`}
    data-testid={`podium-rank-${rank}`}
  >
    <span className="podium-rank font-display">{rank}</span>
    <Trophy className="h-6 w-6" />
    <p className="mt-6 font-display text-2xl font-extrabold text-white" data-testid={`podium-username-${rank}`}>{e?.username ?? "Open spot"}</p>
    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-white/75">{label}</p>
    <p className="font-mono text-lg text-white" data-testid={`podium-wagered-${rank}`}>{fmtUsd(e?.wagered)}</p>
    <p className="podium-prize font-display" data-testid={`podium-prize-${rank}`}>{paid ? `$${PRIZES[rank - 1]}` : "—"}</p>
  </motion.div>
);

export const Leaderboard = () => {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("weighted");
  const [months, setMonths] = useState([]);
  const [current, setCurrent] = useState(null);
  const [month, setMonth] = useState(null);
  const paid = mode === "weighted";
  const past = month !== null;
  useEffect(() => {
    axios.get(`${API}/leaderboard/months`).then((r) => {
      setMonths(Array.isArray(r.data?.months) ? r.data.months : []);
      setCurrent(r.data?.current ?? null);
    }).catch(() => setMonths([]));
  }, []);
  useEffect(() => {
    setData(null);
    axios.get(`${API}/leaderboard`, { params: month ? { month } : {} })
      .then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
    return undefined;
  }, [month]);

  const list = mode === "weighted" ? data?.entries_weighted : data?.entries;
  const entries = Array.isArray(list) ? list : [];
  const rest = Array.from({ length: 17 }, (_, i) => entries[i + 3] ?? { rank: i + 4, username: "—", wagered: 0, prize: paid ? PRIZES[i + 3] ?? 0 : 0 });

  return (
    <section id="leaderboard" className="relative px-5 py-24 sm:px-8 lg:px-12 lg:py-36" data-testid="leaderboard-section">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="eyebrow">Monthly Roobet race</p>
            <h2 className="section-title mt-4 font-display text-white">
              <RevealLine>The <span className="text-[#FFC700]">$500</span></RevealLine>
              <RevealLine delay={0.1}>leaderboard.</RevealLine>
            </h2>
            <p className="mt-5 max-w-md text-base text-white/85">Play on Roobet under code DAFABER. Every dollar you wager this month counts. Top 5 on the weighted board get paid.</p>
          </div>
          <FadeUp className="flex flex-col gap-3">
            {past ? (
              <>
                <p className="text-xs uppercase tracking-[0.25em] text-white/80">Final results</p>
                <p className="font-display text-3xl font-extrabold text-white" data-testid="leaderboard-past-month-label">{monthLabel(month)}</p>
              </>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.25em] text-white/80">Resets in</p>
                <Countdown end={data?.period_end ?? fallbackEnd()} />
              </>
            )}
          </FadeUp>
        </div>

        <FadeUp className="weighted-info mt-14" data-testid="weighted-explainer">
          <Info className="h-6 w-6 shrink-0 text-[#FFC700]" />
          <div>
            <p className="font-display text-lg font-extrabold text-white">How weighted wagering works</p>
            <p className="mt-1 text-sm text-white/85 sm:text-[15px]">
              Leaderboard wager amounts may differ from your statistics on Roobet, depending on the games you are playing:
            </p>
            <ul className="mt-3 space-y-1.5 text-sm text-white sm:text-[15px]" data-testid="weighted-rules">
              <li><span className="inline-block min-w-[3.2rem] font-mono font-bold text-[#FFC700]">100%</span> &nbsp;of wager counted on games with RTP of 97% or lower</li>
              <li><span className="inline-block min-w-[3.2rem] font-mono font-bold text-[#FFC700]">50%</span> &nbsp;of wager counted on games with RTP between 97.01% and 98.99%</li>
              <li><span className="inline-block min-w-[3.2rem] font-mono font-bold text-[#FFC700]">10%</span> &nbsp;of wager counted on games with RTP of 99% and over</li>
            </ul>
            <p className="mt-3 text-sm text-white/85 sm:text-[15px]">
              <b className="text-white">The weighted total decides the leaderboard and the prizes.</b> Any abuse found by DAFABER or Roobet may result in your prize being forfeit.
            </p>
          </div>
        </FadeUp>

        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:gap-6">
          <MonthTabs months={months} current={current} month={month} setMonth={setMonth} />
          <div className="lb-toggle self-start" role="tablist" data-testid="leaderboard-mode-toggle">
          {[["weighted", "Weighted · Prizes"], ["wagered", "Wagered"]].map(([k, l]) => (
            <button key={k} role="tab" aria-selected={mode === k} onClick={() => setMode(k)}
              className={`lb-toggle-btn ${mode === k ? "is-active" : ""}`} data-testid={`leaderboard-mode-${k}`}>
              {mode === k && <motion.span layoutId="lb-pill" className="lb-toggle-pill" transition={{ duration: 0.4, ease: EASE }} />}
              <span className="relative">{l}</span>
            </button>
          ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3 md:items-end">
          {[1, 2, 3].map((r, i) => <Podium key={r} rank={r} e={entries[r - 1]} delay={i * 0.12} label={paid ? "Weighted" : "Wagered"} paid={paid} />)}
        </div>

        <FadeUp className="lb-table mt-8" data-testid="leaderboard-table">
          <div className="lb-row lb-head"><span>#</span><span>Player</span><span className="text-right">{mode === "weighted" ? "Weighted" : "Wagered"}</span><span className="text-right">Prize</span></div>
          {rest.map((e) => (
            <div key={e.rank} className="lb-row" data-testid={`leaderboard-row-${e.rank}`}>
              <span className="font-mono text-white/80">{String(e.rank).padStart(2, "0")}</span>
              <span className="truncate font-semibold text-white">{e.username}</span>
              <span className="text-right font-mono text-white/90">{fmtUsd(e.wagered)}</span>
              <span className={`text-right font-mono font-bold ${e.prize ? "text-[#FFC700]" : "text-white/70"}`}>{e.prize ? `$${e.prize}` : "—"}</span>
            </div>
          ))}
        </FadeUp>
        {data && !entries.length && (
          <p className="mt-6 text-sm text-white/80" data-testid="leaderboard-empty">{past ? "No wagers recorded for this month." : "No wagers yet this month. Be the first on the board."}</p>
        )}
      </div>
    </section>
  );
};
